import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { 
  addExpenseService, 
  updateExpenseService, 
  deleteExpenseService, 
  subscribeToAllExpenses 
} from '../services/expenseService';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/firebase';

const ExpenseContext = createContext();

export const useExpenses = () => {
  const context = useContext(ExpenseContext);
  if (!context) {
    throw new Error('useExpenses must be used within an ExpenseProvider');
  }
  return context;
};

export const ExpenseProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [allExpenses, setAllExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [preferredCurrency, setPreferredCurrency] = useState('INR');
  const [selectedDate, setSelectedDate] = useState({
    month: new Date().getMonth(), // 0-11
    year: new Date().getFullYear(),
  });

  // Calculate start and end dates for the selected month (used for filtering in useMemo)
  const getMonthRange = (year, month) => {
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);
    return { startDate, endDate };
  };

  const userUid = currentUser?.uid;

  // Sync ALL expenses once per session/user
  useEffect(() => {
    if (!userUid) {
      setAllExpenses([]);
      setPreferredCurrency('INR');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Fetch user preferences
    const userRef = doc(db, 'users', userUid);
    const unsubscribeUser = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists() && docSnap.data().currency) {
        setPreferredCurrency(docSnap.data().currency);
      } else {
        setPreferredCurrency('INR');
      }
    });
    
    // Subscribe to ALL user expenses - this powers Home, Reports, and Search instantly
    const unsubscribeExpenses = subscribeToAllExpenses(
      userUid, 
      (fetchedExpenses) => {
        setAllExpenses(fetchedExpenses);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching all expenses:", error);
        setIsLoading(false);
      }
    );

    return () => {
      unsubscribeUser();
      unsubscribeExpenses();
    };
  }, [userUid]);

  // Derived state: Month-specific expenses for Home page
  const expenses = useMemo(() => {
    const { startDate, endDate } = getMonthRange(selectedDate.year, selectedDate.month);
    return allExpenses.filter(expense => {
      const ts = expense.timestamp;
      return ts >= startDate && ts <= endDate;
    });
  }, [allExpenses, selectedDate]);

  // Actions
  const addExpense = useCallback(async (amount, category, note, date) => {
    if (!userUid) throw new Error('User not authenticated');
    return await addExpenseService(userUid, { amount, category, note, date, currency: preferredCurrency });
  }, [userUid, preferredCurrency]);

  const editExpense = useCallback(async (id, updatedData) => {
    if (!userUid) throw new Error('User not authenticated');
    return await updateExpenseService(id, updatedData);
  }, [userUid]);

  const deleteExpense = useCallback(async (id) => {
    if (!userUid) throw new Error('User not authenticated');
    return await deleteExpenseService(id);
  }, [userUid]);

  const updateSelectedDate = useCallback((month, year) => {
    if (month < 0 || month > 11 || year < 1900 || year > 9999) {
      console.warn('Invalid month or year:', { month, year });
      return;
    }
    setSelectedDate({ month, year });
  }, []);

  const value = useMemo(() => ({
    allExpenses,
    expenses,
    preferredCurrency,
    addExpense,
    editExpense,
    deleteExpense,
    setExpenses: setAllExpenses, // Keep setExpenses name for compatibility if needed
    selectedDate,
    updateSelectedDate,
    isLoading,
  }), [allExpenses, expenses, preferredCurrency, addExpense, editExpense, deleteExpense, selectedDate, updateSelectedDate, isLoading]);

  return (
    <ExpenseContext.Provider value={value}>
      {children}
    </ExpenseContext.Provider>
  );
};